/**
 * §7.4 站台結算。
 *
 * 這一頁的核心是 §9 規則 11：
 * 「任一量測項為 null 時，站台結算回『無法結算』，並說明**原因、影響、解法**。
 *  **不得硬湊數字**」
 *
 * 後端的 `/api/work-orders/:id/balance` 已經把三件事都算好回來了，
 * 這裡照著畫——不要在前端自己編一套說法（§9 規則 7 的精神）。
 */

import { useQuery } from '../api/hooks.js'
import { HomogeneityBadge, ResolutionBadge, WeightSourceBadge } from '../components/badges.js'
import { Alert, Card, Empty, KV, Mono, Pill, Table, Td } from '../components/ui.js'
import { useDict } from '../config/ConfigContext.js'
import { kg, kgUnit, pct, count, ts } from '../../../shared/lib/format.js'
import { Link } from '../router.js'

interface Side {
  materialCode: string
  kg: number | null
  weightSource: 'measured' | 'booked' | 'derived'
  panelCount: number
  note: string
  measurementPointId: string | null
  measurementDisabled: boolean
}

interface Balance {
  workOrder: {
    workOrderId: string; stationId: string; resourceId: string; plannedCount: number
    modelCode: string | null; purged: boolean; operatorId: string
    openedAt: string; closedAt: string | null; status: string
  } | null
  issued: Side[]
  received: Side[]
  expectedOutputs: string[]
  missingOutputs: string[]
  settleable: boolean
  /** 後端已經附上原因、影響、解法三件事 */
  blocking: { materialCode: string; measurementPointId: string | null; cause: string; impact: string; remedy: string }[]
  issuedTotalKg: number | null
  receivedTotalKg: number | null
  lossKg: number | null
  lossPct: number | null
  lossPass: boolean | null
  tolerancePct: number
  compositionChecks: { materialCode: string; actualPct: number; theoreticalPct: number; tolerancePct: number; pass: boolean }[]
  negativeLoss: boolean
}

export function StationBalance({ workOrderId }: { workOrderId: string }) {
  const dict = useDict()
  const { data, error, loading } = useQuery<Balance>(
    workOrderId ? `/api/work-orders/${encodeURIComponent(workOrderId)}/balance` : null,
  )

  if (!workOrderId) return <Alert tone="warn" title="缺少工單號">請從工單清單或單片追溯進來。</Alert>
  if (loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (error) return <Alert tone="danger" title="查不到這張工單">{error.message}</Alert>
  if (!data?.workOrder) return null

  const wo = data.workOrder

  return (
    <div className="space-y-4">
      <Card
        title={<>站台結算 <Mono>{wo.workOrderId}</Mono></>}
        subtitle={`${dict.station(wo.stationId)} · ${dict.resource(wo.resourceId)} · 操作員 ${wo.operatorId}`}
        right={<Pill tone={wo.status === 'open' ? 'sky' : 'ink'}>{wo.status === 'open' ? '進行中' : '已關閉'}</Pill>}
      >
        <KV items={[
          ['預計片數', count(wo.plannedCount)],
          ['型號', wo.modelCode ?? '未指定'],
          ['清機狀態', wo.purged ? '已清機' : '未清機（負損耗為預期行為）'],
          ['開立 / 關閉', `${ts(wo.openedAt)}${wo.closedAt ? ` → ${ts(wo.closedAt)}` : ''}`],
        ]} />
      </Card>

      {/* ── §9 規則 11：無法結算 ＋ 原因 ＋ 影響 ＋ 解法 ──────────────── */}
      {!data.settleable && (
        <Card title="無法結算" tone="warn">
          <p className="text-sm text-ink-700 mb-3">
            這張工單有 {data.blocking.length} 項還不能算。
            系統**不會**替您把數字湊出來——湊出來的損耗沒有意義，而且會蓋掉真正的問題。
          </p>
          <div className="space-y-3">
            {data.blocking.map((b, i) => (
              <div key={i} className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-900">
                  {dict.material(b.materialCode)}
                  {b.measurementPointId && (
                    <Mono className="ml-2 text-amber-700">{b.measurementPointId}</Mono>
                  )}
                </p>
                <dl className="mt-2 grid grid-cols-[3.5rem,1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-amber-700">原因</dt><dd className="text-amber-900">{b.cause}</dd>
                  <dt className="text-amber-700">影響</dt><dd className="text-amber-900">{b.impact}</dd>
                  <dt className="text-amber-700">解法</dt><dd className="text-amber-900">{b.remedy}</dd>
                </dl>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.settleable && (
        <Card
          title="結算"
          tone={data.negativeLoss ? 'warn' : data.lossPass === false ? 'danger' : 'ok'}
        >
          <KV items={[
            ['領出合計', kgUnit(data.issuedTotalKg, 2)],
            ['繳回合計', kgUnit(data.receivedTotalKg, 2)],
            ['損耗', (
              <span className="tabular-nums font-semibold">
                {kgUnit(data.lossKg, 3)}
                {data.lossPct !== null && <span className="ml-2 text-xs font-normal">（{pct(data.lossPct)}）</span>}
              </span>
            )],
            ['容差', pct(data.tolerancePct, 1)],
          ]} />

          {data.negativeLoss && (
            <div className="mt-3">
              <Alert tone="warn" title="負損耗：繳回大於領出">
                {wo.purged
                  ? '這張工單標記為已清機，繳回卻大於領出——請查核量測（可能是前一批的殘料混進來，或秤有偏移）。'
                  : '這張工單未清機，繳回大於領出是預期行為（前一批的殘留被帶出來了）。'}
              </Alert>
            </div>
          )}
          {data.lossPass === false && !data.negativeLoss && (
            <div className="mt-3">
              <Alert tone="danger" title="損耗超出容差">
                損耗 {pct(data.lossPct)} 超過容差 {pct(data.tolerancePct, 1)}。請查核量測與是否有漏繳庫。
              </Alert>
            </div>
          )}
        </Card>
      )}

      <Card title="投入（領料）">
        {data.issued.length === 0 ? <Empty>這張工單還沒領料。</Empty> : (
          <Table head={['物料', '重量', '片數', '來源', '量測點', '說明']}>
            {data.issued.map((s, i) => <SideRow key={i} s={s} dict={dict} />)}
          </Table>
        )}
      </Card>

      <Card
        title="產出（繳庫）"
        subtitle={data.expectedOutputs.length > 0
          ? `本站應產出：${data.expectedOutputs.map((c) => dict.material(c)).join('、')}`
          : undefined}
      >
        {data.received.length === 0 ? <Empty>這張工單還沒繳庫。</Empty> : (
          <Table head={['物料', '重量', '片數', '來源', '量測點', '說明']}>
            {data.received.map((s, i) => <SideRow key={i} s={s} dict={dict} />)}
          </Table>
        )}
        {data.missingOutputs.length > 0 && (
          <div className="mt-3">
            <Alert tone="warn" title="有應繳庫項目還沒繳">
              {data.missingOutputs.map((c) => dict.material(c)).join('、')}
              ——本站的產出不完整，帳上會少一段物料。
            </Alert>
          </div>
        )}
      </Card>

      {data.compositionChecks.length > 0 && (
        <Card title="拆出流檢核" subtitle="各產出佔比 vs 型號理論佔比">
          <Table head={['物料', '實際佔比', '理論佔比', '容差', '判定']}>
            {data.compositionChecks.map((c) => (
              <tr key={c.materialCode} className={c.pass ? '' : 'bg-amber-50/40'}>
                <Td>{dict.material(c.materialCode)}</Td>
                <Td className="tabular-nums">{pct(c.actualPct)}</Td>
                <Td className="tabular-nums text-ink-500">{pct(c.theoreticalPct)}</Td>
                <Td className="tabular-nums text-ink-500">±{pct(c.tolerancePct, 0)}</Td>
                <Td>{c.pass
                  ? <Pill tone="emerald">通過</Pill>
                  : <Pill tone="amber">偏離理論值</Pill>}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </div>
  )
}

function SideRow({ s, dict }: { s: Side; dict: ReturnType<typeof useDict> }) {
  const mp = s.measurementPointId ? dict.measurementPoint(s.measurementPointId) : null
  return (
    <tr className={s.kg === null ? 'bg-amber-50/40' : ''}>
      <Td>{dict.material(s.materialCode)}</Td>
      <Td className="tabular-nums">
        {s.kg === null
          ? <Pill tone="amber">{s.measurementDisabled ? '量測點停用' : '未輸入'}</Pill>
          : kgUnit(s.kg, mp?.decimals ?? 2)}
      </Td>
      <Td className="tabular-nums">{count(s.panelCount)}</Td>
      <Td><WeightSourceBadge src={s.weightSource === 'derived' ? 'booked' : s.weightSource} /></Td>
      <Td className="text-xs">
        {s.measurementPointId ? <Mono>{s.measurementPointId}</Mono> : '—'}
        {mp && <span className="ml-1 text-ink-400">{mp.granularity === 'panel' ? '逐片' : '整批'}</span>}
      </Td>
      <Td className="text-xs text-ink-500">{s.note}</Td>
    </tr>
  )
}
